import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserIcon, LockIcon, EyeIcon, EyeOffIcon } from '../UI/Icons';
import { ValidationConstants, ValidationUtils } from '../../utils';
import './LoginPage.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<{email?: string; password?: string}>({});
  
  const { login } = useAuth();

  const validateForm = (): boolean => {
    const errors: {email?: string; password?: string} = {};

    if (!email.trim()) {
      errors.email = 'Email обов\'язковий';
    } else if (!ValidationUtils.isValidEmail(email)) {
      errors.email = 'Невірний формат email';
    } else if (email.length > ValidationConstants.User.EmailMaxLength) {
      errors.email = `Email не може перевищувати ${ValidationConstants.User.EmailMaxLength} символів`;
    }

    if (!password) {
      errors.password = 'Пароль обов\'язковий';
    } else if (password.length > ValidationConstants.User.PasswordMaxLength) {
      errors.password = `Пароль не може перевищувати ${ValidationConstants.User.PasswordMaxLength} символів`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setValidationErrors({});

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.errors?.join(', ') || 'Помилка входу');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">Finance Tracker</h1>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="login-email">Електронна пошта</label>
            <div className="input-wrapper">
              <div className="input-icon input-icon-left">
                <UserIcon size={20} color="rgba(255, 255, 255, 0.6)" />
              </div>
              <input
                type="email"
                id="login-email"
                name="email"
                className={validationErrors.email ? 'form-input error' : 'form-input'}
                placeholder="Введіть вашу електронну пошту"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={ValidationConstants.User.EmailMaxLength}
                data-form-type="other"
              />
            </div>
            {validationErrors.email && (
              <span className="error-message">{validationErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="login-password">Пароль</label>
            <div className="input-wrapper">
              <div className="input-icon input-icon-left">
                <LockIcon size={20} color="rgba(255, 255, 255, 0.6)" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                id="login-password"
                name="password"
                className={validationErrors.password ? 'form-input error' : 'form-input'}
                placeholder="Введіть ваш пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                maxLength={ValidationConstants.User.PasswordMaxLength}
                autoComplete="new-password"
                data-form-type="other"
              />
              {password.length > 0 && (
                <div
                  className="input-icon input-icon-right"
                  onClick={() => setShowPassword(!showPassword)}
                  role="button"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Приховати пароль' : 'Показати пароль'}
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? (
                    <EyeOffIcon size={20} color="rgba(255, 255, 255, 0.6)" />
                  ) : (
                    <EyeIcon size={20} color="rgba(255, 255, 255, 0.6)" />
                  )}
                </div>
              )}
            </div>
            {validationErrors.password && (
              <span className="error-message">{validationErrors.password}</span>
            )}
          </div>

          {error && (
            <div className="alert alert-error show">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="btn btn-primary btn-full" 
            disabled={isLoading}
          >
            {isLoading ? (
              <span>Виконується вхід...</span>
            ) : (
              <>
                <span>Увійти</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
